import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { InvoiceService } from 'src/app/business-service/invoice/invoice.service';
import { LeadsService } from 'src/app/business-service/leads/leads.service';
import { PaymentService } from 'src/app/business-service/payments/payment.service';
import { ResponseNotificationService } from '../../shared/services/response-notification/response-notification.service';
import { ComponentReloadService } from '../../shared/services/reload/component-reload.service';
import { StatusUpdateService } from 'src/app/business-service/status-update/status-update.service';
import { ENTITY_NAMES, INVOICE_TYPES, RECORD_STATUS_VALUES } from '../../shared/constants/api-constants';
import { GlobalService } from '../../shared/services/global/global.service';
import { InputValidationsService } from '../../shared/services/validations/input-validations.service';

@Component({
  selector: 'app-payment-form',
  templateUrl: './payment-form.component.html',
  styleUrls: ['./payment-form.component.scss'],
})
export class PaymentFormComponent implements OnInit {

  invoiceId: any;
  leadId: any;

  isPaymentRaisedAgainstLead: boolean = false;
  amountReceviedAgainstLead: any = 0;

  invoiceType: string = "";

  paymentForm!: FormGroup;

  forRefund: boolean = false;

  selectedCurrency: string = '';

  @Output() formSubmitted: EventEmitter<any> = new EventEmitter();

  INVOICE_TYPES = INVOICE_TYPES;

  constructor(
    private _modalController: ModalController,
    private _invoiceService: InvoiceService,
    private _fb: FormBuilder,
    private _paymentService: PaymentService,
    private _responseNotification: ResponseNotificationService,
    private _reloadService: ComponentReloadService,
    private _statusUpdateService: StatusUpdateService,
    private _globalService: GlobalService,
    private _validationService: InputValidationsService
  ) { }

  ngOnInit() {
    this.paymentForm = this._fb.group({
      clientName: [''],
      invoiceNumber: [''],
      totalAmount: [''],
      currentBalanceAmount: [''],
      paymentMethod: ['', Validators.required],
      requestedAmount: ['', [Validators.required]],
      updatedBalanceAmount: ['', [Validators.required, this.valueNotNegativeValidator()]],
      paymentReferenceNumber: [''],
      paymentDate: [this.todayDate, Validators.required],
      paymentNotes: [''],
      leadId: ['']
    });

    if (this.invoiceId) {
      if (this.invoiceType === INVOICE_TYPES.CUSTOMER) {
        this._invoiceService.getInvoiceDetails(this.invoiceId).subscribe((invoiceData) => {
          this.patchInvoiceAndLeadDetails(invoiceData);
        })
      } else if (this.invoiceType === INVOICE_TYPES.VENDOR) {
        this._invoiceService.getVendorInvoiceDetails(this.invoiceId).subscribe((invoiceData) => {
          this.patchInvoiceAndLeadDetails(invoiceData);
        })
      }

    }

    if (this.forRefund) {
      this.paymentForm.get("paymentMethod")?.setValue("Refund");
      this.paymentForm.get("paymentMethod")?.disable();
    }

    // case when payment is raised against lead
    if (this.leadId) {
      this.isPaymentRaisedAgainstLead = true;
      this._paymentService.getLatestPaymentOfLead(this.leadId).subscribe({
        next: (res) => {
          if (res?.status === false) {
            return
          }
          this.amountReceviedAgainstLead = res?.updatedBalanceAmount || 0;
          this.paymentForm.patchValue({
            updatedBalanceAmount: this.amountReceviedAgainstLead,
          })
        }
      })
    }

    // Apply custom validator after form is created
    if (!this.isPaymentRaisedAgainstLead) {
      this.setRequestedAmountValidator();
    }

    // Value change subscription
    this.paymentForm.get('requestedAmount')?.valueChanges.subscribe(() => {
      this.updateNewBalance();
    });

    // Re-apply validator if relevant values change
    this.paymentForm.get('currentBalanceAmount')?.valueChanges.subscribe(() => {
      this.setRequestedAmountValidator();
    });
    this.paymentForm.get('totalAmount')?.valueChanges.subscribe(() => {
      this.setRequestedAmountValidator();
    });
  }

  valueNotNegativeValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = +control.value;
      if (isNaN(value) || value < 0) {
        return { negativeValue: true };
      }
      return null;
    }
  }

  get todayDate(): Date {
    let date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  convertToDate(value: any): Date {
    return new Date(value);
  }

  setRequestedAmountValidator() {
    const requestedAmountControl = this.paymentForm.get('requestedAmount');
    if (requestedAmountControl) {
      requestedAmountControl.setValidators([
        Validators.required,
        this.customValidatorForRequestedAmount()
      ]);
      requestedAmountControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  customValidatorForRequestedAmount(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = +control.value;
      if (isNaN(value)) return { invalidAmount: true };

      const currentBalance = +this.paymentForm.get('currentBalanceAmount')?.value || 0;
      const totalAmount = +this.paymentForm.get('totalAmount')?.value || 0;
      let max = 0;

      if (this.forRefund) {
        // Amount already paid = totalAmount - currentBalance
        max = totalAmount - currentBalance;
      } else {
        // Max is current balance
        max = currentBalance;
      }

      if (value < 0 || value > max) {
        return { amountOutOfRange: { min: 0, max } };
      }
      return null;
    };
  }

  patchInvoiceAndLeadDetails(invoiceData: any) {
    this.selectedCurrency = invoiceData.currencyCode || '';
    this.paymentForm.patchValue({
      clientName: invoiceData.firstName + " " + invoiceData.lastName,
      invoiceNumber: invoiceData.invoiceId,
      totalAmount: invoiceData.totalAmount,
      currentBalanceAmount: invoiceData.balanceAmount
    })
    this.paymentForm.get("clientName")?.disable();
    this.paymentForm.get("invoiceNumber")?.disable();
    this.paymentForm.get("totalAmount")?.disable();
    this.paymentForm.get("currentBalanceAmount")?.disable();
    this.paymentForm.get("updatedBalanceAmount")?.disable();
  }

  updateNewBalance() {
    const curBal = +(this.paymentForm.get("currentBalanceAmount")?.value);
    const reqAmt = +(this.paymentForm.get('requestedAmount')?.value);
    let newBal = 0

    if (this.isPaymentRaisedAgainstLead) {
      if (this.forRefund) {
        newBal = +this.amountReceviedAgainstLead - +reqAmt;
      } else {
        newBal = +this.amountReceviedAgainstLead + +reqAmt;
      }
    } else if (this.forRefund) {
      newBal = +curBal + +reqAmt;
    } else {
      newBal = curBal - reqAmt;
    }

    this.paymentForm.get("updatedBalanceAmount")?.setValue(this.formatToTwoDecimals(newBal))

  }

  formatToTwoDecimals(input: string | number): string {
    if (input === null || input === undefined || input === '') {
      return '';
    }

    // Convert to number safely
    const num = parseFloat(String(input));

    if (isNaN(num)) {
      return '';
    }

    // Fix to 2 decimals
    return num.toFixed(2);
  }



  closeModal() {
    this._modalController.dismiss();
  }

  async onSubmit() {
    if (this.paymentForm.valid) {
      let flag = await this._globalService.confirmActionPopup("Payment once added cannot be edited or deleted. Please confirm to proceed.", "Confirm");
      if (!flag) {
        return;
      }
      // Conditoion to check if payment is for customer invoice or vendor invoice, as API is different for both cases
      if (this.invoiceType === INVOICE_TYPES.CUSTOMER) {
        this.serializeFormData(this.paymentForm.getRawValue()).then(formData => {
          // If payment is raised against lead, then must call a differenct API to create payment
          if (this.isPaymentRaisedAgainstLead) {
            this._paymentService.createPaymentAgainstLead(formData).subscribe((data) => {
              this._responseNotification.showMessage("Payment added successfully!")
              this.closeModal();
              this._reloadService.triggerPaymentReload()
            }, (error) => {
              this._responseNotification.showMessage("Something went wrong, please try again", true);
            })
          } else {
            this._paymentService.createPayment(formData).subscribe((data) => {
              this._responseNotification.showMessage("Payment added successfully!")
              this.closeModal();
              this._reloadService.triggerPaymentReload()

              if (data.updatedBalanceAmount === 0) {
                this._statusUpdateService.updateStatusOfRecord(ENTITY_NAMES.INVOICE, data.invoiceId, RECORD_STATUS_VALUES.COMPLETED).subscribe((data) => {
                  this._reloadService.triggerInvoicesReload()
                })
              }
            }, (error) => {
              this._responseNotification.showMessage("Something went wrong, please try again", true);
            })
          }
        })

      } else if (this.invoiceType === INVOICE_TYPES.VENDOR) {
        this.serializeVendorPaymentFormData(this.paymentForm.getRawValue()).then(formData => {
          this._paymentService.createVendorPayment(formData).subscribe((data) => {
            this._responseNotification.showMessage("Payment added successfully!")
            this.closeModal();
            this._reloadService.triggerPaymentReload();

            if (data.closingBalanceAmount === 0) {
              this._statusUpdateService.updateStatusOfRecord(ENTITY_NAMES.VENDOR_INVOICE, data.invoiceId, RECORD_STATUS_VALUES.COMPLETED).subscribe((data) => {
                this._reloadService.triggerInvoicesReload()
              })
            }
          }, (error) => {
            this._responseNotification.showMessage("Something went wrong, please try again", true);
          })
        })
      }
    } else {
      this.paymentForm.markAllAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      this._responseNotification.showMessage("Form is invalid, please fill all required fields.", true);
    }
  }

  private async serializeFormData(formData: any) {
    let serializedData: any = {
      invoiceId: formData.invoiceNumber || null,
      paymentDate: this._globalService.convertDateToString(this.convertToDate(formData.paymentDate)),
      paymentMethod: formData.paymentMethod,
      totalAmount: formData.totalAmount,
      balanceAmount: formData.currentBalanceAmount,
      requestedAmount: formData.requestedAmount,
      updatedBalanceAmount: formData.updatedBalanceAmount,
      referenceNumber: formData.paymentReferenceNumber,
      payerName: formData.clientName,
      notes: formData.paymentNotes
    }

    if (this.isPaymentRaisedAgainstLead) {
      serializedData = {
        ...serializedData,
        leadId: this.leadId,
      }
    }

    return serializedData;
  }

  private async serializeVendorPaymentFormData(formData: any) {
    let serializedData: any = {
      invoiceId: formData.invoiceNumber || null,
      paymentDate: this._globalService.convertDateToString(this.convertToDate(formData.paymentDate)),
      paymentMethod: formData.paymentMethod,
      amount: formData.requestedAmount,
      closingBalanceAmount: formData.updatedBalanceAmount,
      referenceNumber: formData.paymentReferenceNumber,
      notes: formData.paymentNotes
    }

    return serializedData;
  }

}
